import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, MapPin, ExternalLink, Loader2, Users, Calendar } from "lucide-react";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import { useI18n } from "../i18n/I18nContext";
import { api } from "../lib/nailApi";

/**
 * Wall of Founders — public page listing organizers who completed onboarding.
 * Drives social proof for new signups + community recognition.
 */
export default function WallOfFoundersPage() {
  const { lang } = useI18n();
  const [founders, setFounders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get("/organizers/wall")
      .then((r) => setFounders(r.data.founders || []))
      .catch((e) => setError(e?.response?.data?.detail || "Không tải được danh sách."))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-white text-charcoal" data-testid="wall-of-founders-page">
      <SiteHeader variant="light" />

      <main className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10 py-14 sm:py-20 md:py-24">
        <p className="text-[11px] uppercase tracking-[0.32em] text-emerald2 font-semibold mb-4 sm:mb-5">
          {lang === "en" ? "Wall of Founders" : "Bức tường nhà sáng lập"}
        </p>
        <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-charcoal max-w-3xl">
          {lang === "en"
            ? "The organizers building the Nail Viet community"
            : "Những nhà tổ chức đang xây dựng cộng đồng Nail Viet"}
        </h1>
        <p className="mt-4 text-warmgray-700 max-w-2xl">
          {lang === "en"
            ? "These organizers have completed their full setup on Nail Viet Club and are hosting events for the community."
            : "Những nhà tổ chức đã hoàn tất thiết lập trên Nail Viet Club và đang tổ chức sự kiện cho cộng đồng."}
        </p>

        <div className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-emerald2 font-semibold">
          <Users className="w-3.5 h-3.5" />
          <span data-testid="founders-count">
            {loading ? (lang === "en" ? "Loading…" : "Đang tải…") : `${founders.length} ${lang === "en" ? "founders" : "nhà tổ chức"}`}
          </span>
        </div>

        {/* Body */}
        <div className="mt-10 sm:mt-12">
          {loading ? (
            <div className="text-center py-16 text-warmgray-500" data-testid="founders-loading">
              <Loader2 className="w-6 h-6 animate-spin mx-auto" />
              <p className="mt-3 text-sm">{lang === "en" ? "Loading founders…" : "Đang tải nhà tổ chức…"}</p>
            </div>
          ) : error ? (
            <div className="rounded-3xl border border-red-200 bg-red-50 p-6 text-red-800 text-sm" data-testid="founders-error">
              {error}
            </div>
          ) : founders.length === 0 ? (
            <EmptyState lang={lang} />
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6" data-testid="founders-grid">
              {founders.map((f) => (
                <FounderCard key={f.id} founder={f} lang={lang} />
              ))}
            </div>
          )}
        </div>

        {/* Join CTA */}
        <div className="mt-16 sm:mt-20 rounded-3xl bg-charcoal-900 text-white p-8 sm:p-12 relative overflow-hidden">
          <div className="absolute inset-0 summit-gradient opacity-80 pointer-events-none" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-gold-300 font-semibold">
              <Sparkles className="w-3.5 h-3.5" />
              {lang === "en" ? "Be the next founder" : "Trở thành nhà sáng lập tiếp theo"}
            </div>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl max-w-2xl">
              {lang === "en"
                ? "Host your next event on Nail Viet Club"
                : "Tổ chức sự kiện tiếp theo của bạn trên Nail Viet Club"}
            </h2>
            <p className="mt-3 text-white/70 max-w-xl">
              {lang === "en"
                ? "Free signup. Full setup in under 10 minutes. Premium check-in kiosk + AI advisor included."
                : "Đăng ký miễn phí. Thiết lập đầy đủ trong dưới 10 phút. Bao gồm kiosk check-in cao cấp + Cố Vấn AI."}
            </p>
            <Link
              to="/organizer/signup"
              data-testid="founders-cta-signup"
              className="mt-6 inline-flex items-center gap-2 bg-gold-500 text-charcoal rounded-full px-6 py-3 font-semibold hover:bg-gold-400 transition-all"
            >
              {lang === "en" ? "Start hosting" : "Bắt đầu tổ chức"}
            </Link>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}

function FounderCard({ founder, lang }) {
  const { name, slug, city, state, logo_url, description, featured_event } = founder;
  const placeText = [city, state].filter(Boolean).join(", ");

  return (
    <article
      data-testid={`founder-card-${slug}`}
      className="group rounded-3xl bg-white border border-warmgray-200 hover:border-emerald2/40 hover:-translate-y-0.5 transition-all overflow-hidden flex flex-col"
    >
      <div className="p-5 sm:p-6 flex items-start gap-4">
        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-warmgray-50 border border-warmgray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
          {logo_url ? (
            <img src={logo_url} alt={name} className="w-full h-full object-contain" />
          ) : (
            <Sparkles className="w-6 h-6 text-emerald2" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-serif text-lg sm:text-xl text-charcoal leading-tight truncate" data-testid={`founder-name-${slug}`}>
            {name}
          </h3>
          {placeText && (
            <div className="mt-1 inline-flex items-center gap-1 text-xs text-warmgray-500">
              <MapPin className="w-3 h-3" /> {placeText}
            </div>
          )}
        </div>
      </div>

      {description && (
        <p className="px-5 sm:px-6 pb-3 text-sm text-warmgray-700 leading-relaxed line-clamp-3">
          {description}
        </p>
      )}

      {featured_event ? (
        <Link
          to={`/events/${featured_event.slug}`}
          data-testid={`founder-event-${slug}`}
          className="mt-auto border-t border-warmgray-100 bg-warmgray-50/60 hover:bg-warmgray-50 px-5 sm:px-6 py-3 flex items-center justify-between gap-2"
        >
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-emerald2 font-semibold flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {lang === "en" ? "Upcoming event" : "Sự kiện sắp tới"}
            </div>
            <div className="mt-0.5 text-sm font-medium text-charcoal truncate">
              {featured_event.name}
            </div>
            <div className="text-[11px] text-warmgray-500 truncate">
              {featured_event.event_date}{featured_event.location ? ` · ${featured_event.location}` : ""}
            </div>
          </div>
          <ExternalLink className="w-4 h-4 text-warmgray-400 group-hover:text-emerald2 flex-shrink-0" />
        </Link>
      ) : (
        <div className="mt-auto border-t border-warmgray-100 px-5 sm:px-6 py-3 text-[11px] text-warmgray-500 italic">
          {lang === "en" ? "Event coming soon" : "Sự kiện sắp ra mắt"}
        </div>
      )}
    </article>
  );
}

function EmptyState({ lang }) {
  return (
    <div className="rounded-3xl bg-warmgray-50 border border-warmgray-200 p-10 sm:p-14 text-center" data-testid="founders-empty">
      <Sparkles className="w-8 h-8 text-emerald2/60 mx-auto" />
      <h3 className="mt-4 font-serif text-2xl text-charcoal">
        {lang === "en" ? "Be the first founder" : "Trở thành nhà sáng lập đầu tiên"}
      </h3>
      <p className="mt-2 text-warmgray-700 max-w-md mx-auto">
        {lang === "en"
          ? "No organizer has hit 100% yet. Sign up, complete your setup, and your salon could be the first on the wall."
          : "Chưa có nhà tổ chức nào đạt 100%. Hãy đăng ký, hoàn tất thiết lập, và bạn có thể là tên đầu tiên trên bức tường này."}
      </p>
      <Link
        to="/organizer/signup"
        data-testid="founders-empty-cta"
        className="mt-6 inline-flex items-center gap-2 bg-emerald2 text-white rounded-full px-5 py-2.5 font-medium hover:-translate-y-0.5 transition-all"
      >
        {lang === "en" ? "Start your setup" : "Bắt đầu thiết lập"}
      </Link>
    </div>
  );
}
